import { jwtDecode } from "jwt-decode";
import { useGetMyPermissionsQuery } from "../store/services/adminAccessServices";

/* Central access check for the dashboard.
 *
 *   can(module, 'view' | 'manage') -> boolean
 *
 * ---------------------------------------------------------------------------
 * IT NARROWS THE `admin` ROLE AND NOTHING ELSE — mirroring the backend's
 * middleware/checkPermission.js exactly, because a UI that disagreed with the
 * server would either hide sections that work or show sections that 403.
 *
 *   super_admin   sees everything; it is the role that hands grants out.
 *   admin         governed by the admin_permissions matrix.
 *   marketing     unchanged. Its access is decided by the `roles` arrays in
 *   company_admin Menu.js MENU_GROUPS and by the company guards on the API, so
 *   agent         can() returns true and lets that existing gating decide. This
 *                 is what stops a per-module matrix built for platform admins
 *                 from quietly cutting an agency's team off from its own work.
 *
 * The role is read from the JWT first so the sidebar renders instantly, and
 * confirmed by /users/me/permissions once it loads.
 */
export function usePermissions() {
    const token = (typeof window !== "undefined" && localStorage.getItem("prona_userToken")) || null;

    let decoded = null;
    try {
        decoded = token ? jwtDecode(token) : null;
    } catch (e) {
        decoded = null;
    }

    const { data, isLoading, isFetching, isError } = useGetMyPermissionsQuery(undefined, {
        skip: !token,
    });

    /* The API's role wins once it arrives: the token is issued at login and a
       role changed since then would otherwise stay stale until it expired. */
    const role = data?.role || decoded?.role || null;
    const isSuperAdmin = role === "super_admin";
    const isScopedAdmin = role === "admin";

    const permissions = data?.permissions || {};

    /* If the permissions call fails, a scoped admin keeps full UI access rather
       than being locked out of a dashboard that may be perfectly usable. This is
       safe because it is only the UI: every endpoint is enforced server-side, so
       a module they truly lack still answers 403 when they open it. */
    const failedOpen = isScopedAdmin && isError;

    const can = (moduleKey, level = "view") => {
        if (isSuperAdmin) return true;
        if (!isScopedAdmin) return true;
        if (failedOpen) return true;

        const grant = permissions[moduleKey];
        if (!grant) return false;
        return level === "manage" ? !!grant.can_manage : !!(grant.can_view || grant.can_manage);
    };

    /* super_admin and the non-scoped roles need no network call to be usable. */
    const ready = !token || !isScopedAdmin || (!!data && !isFetching) || isError;

    return {
        role,
        isSuperAdmin,
        isScopedAdmin,
        can,
        permissions,
        isLoading: isLoading || isFetching,
        ready,
    };
}

export default usePermissions;
